import { useClerk } from "@clerk/tanstack-react-start";
import { useNavigate, useBlocker } from "@tanstack/react-router";
import React, { useState, useRef } from "react";

import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { setUserPassword } from "~/lib/functions/user";
import { useUpdateUserProfile } from "~/lib/queries/userQuery";

export function OnboardingModal() {
  const clerk = useClerk();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedRef = useRef(false);

  useBlocker({
    shouldBlockFn: () => !submittedRef.current,
    withResolver: false,
  });

  const updateProfile = useUpdateUserProfile();

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    if (password !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await updateProfile.mutateAsync({
        firstName,
        lastName,
        email: clerk.user?.primaryEmailAddress?.emailAddress ?? "",
        phoneNumber: undefined,
        photo: undefined,
        title: undefined,
        bio: undefined,
        clerkUserId: clerk.user?.id,
      });

      await setUserPassword({ data: { password } });

      submittedRef.current = true;
      await navigate({ to: "/coaching" });
    } catch (error) {
      setErrors({
        root:
          error instanceof Error
            ? error.message
            : "An error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <h2 className="mb-1 font-heading text-xl font-medium text-gray-900">
          Complete Your Registration
        </h2>
        <p className="text-black-700 mb-6 text-sm">
          Please fill in your details to access the platform.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="First Name" error={errors.firstName}>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              error={errors.firstName}
              className="h-13"
            />
          </FormField>

          <FormField label="Last Name" error={errors.lastName}>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              error={errors.lastName}
              className="h-13"
            />
          </FormField>

          <FormField label="New Password" error={errors.password}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter a password (min. 8 characters)"
              error={errors.password}
              className="h-13"
            />
          </FormField>

          <FormField label="Confirm Password" error={errors.confirmPassword}>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              error={errors.confirmPassword}
              className="h-13"
            />
          </FormField>

          {errors.root && <p className="text-sm text-red-600">{errors.root}</p>}

          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            variant="primary"
            className="mt-2 text-base"
          >
            Complete Registration
          </Button>
        </form>
      </div>
    </div>
  );
}
